import { useEffect, useMemo, useState } from "react";

import clsx from "clsx";
import { AnimatePresence } from "framer-motion";
import { isEqual, keyBy, orderBy } from "lodash";
import moment from "moment";
import { CSVDownloader, CSVReader } from "react-papaparse";
import { useToasts } from "react-toast-notifications";

import { useUser } from "@components/user-context";
import {
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
} from "@components/v2/buttons";
import { TextInput } from "@components/v2/inputs";
import { FETCH_SPEND_DATA_COLUMNS } from "@gql/mmm-scenario-modeling";
import { useNorthbeamMutation, useNorthbeamQuery } from "@hooks/http";
import {
  type CreateCustomBudgetMix,
  type CreateCustomBudgetMixVariables,
  type FetchCustomScenarioData_me_mmm_customScenarioData,
  type SpendDataColumns,
  type SpendDataColumnsVariables,
} from "@north-beam/nb-client-side";
import { MMMClientState } from "@north-beam/nb-common";
import { CreateCustomBudgetTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { BUDGET_MIXES_FOR_CYCLE_QUERY } from "@pages/mmm/explore-budgets-v2/explore-budgets-queries";
import { useClientKpis } from "@pages/mmm/hooks/use-client-kpis";
import { useLoadBudgetMixes } from "@pages/mmm/hooks/use-load-budget-mixes";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";
import { Modal } from "@shared/modal";

import { KpiSelect } from "../../shared/kpi-select";
import { CREATE_CUSTOM_BUDGET_MIX } from "../adopt-mix-mutation";
import { useMMMAdminMode } from "../budget-mix-table/utils";
import { type Kpi } from "../client-setting/types";

import { ForecastChart } from "./forecast-chart";
import { type PredictionData, type UploadedDataRecord } from "./types";
import { useCustomScenarioData } from "./use-custom-scenario-data";
import { formatScenarioModellingDataAsBudgetMix, getPeriods } from "./utils";

const TEMPLATE_DATE_FORMAT = "MMM DD, YYYY";

interface SelectedFile {
  name: string;
  uploader: string;
  data: any;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const AddSpendDataModal = ({ isOpen, onClose }: Props) => {
  const { user } = useUser();
  const { loadBudgetMixData, isBudgetMixDataLoading } = useLoadBudgetMixes();
  const trackMMMEvent = useTrackMMMEvent(MMMTab.CREATE_CUSTOM_BUDGETS);
  const { selectedCycle } = useSelectedCycle();
  const { kpis } = useClientKpis();
  const kpisByName = useMemo(() => keyBy(kpis, "kpi_name"), [kpis]);
  const [isViewForecastStep, setIsViewForecastStep] = useState(false);
  const isAdminMode = useMMMAdminMode(user);
  const [selectedKpiName, setSelectedKpiName] = useState(kpis[0].kpi_name);
  const [budgetMixName, setBudgetMixName] = useState("");
  const [prediction, setPrediction] = useState<PredictionData | null>(null);
  const { addToast } = useToasts();
  const {
    customScenarioData,
    createCustomScenarioData,
    isCreateCustomScenarioDataLoading,
    updateCustomScenarioData,
    isUpdateCustomScenarioLoading,
    deleteCustomScenarioData,
  } = useCustomScenarioData();

  const dateRange = useMemo(
    () => ({
      startDate: moment(selectedCycle.date_range.start_date),
      endDate: moment(selectedCycle.date_range.end_date),
    }),
    [selectedCycle],
  );

  const [selectedAddToMixesRow, setSelectedAddToMixesRow] =
    useState<FetchCustomScenarioData_me_mmm_customScenarioData | null>(null);

  const onAddUpload = async (file: SelectedFile) => {
    trackMMMEvent({
      component: CreateCustomBudgetTrackedComponent.UPLOAD_DATA_MODAL as any,
      action: "onSubmit",
    });

    const matchingScenario = customScenarioData.find(
      (e) => e.name === file.name,
    );

    if (
      matchingScenario &&
      matchingScenario.name === file.name &&
      isEqual(matchingScenario.data, file.data)
    ) {
      setSelectedAddToMixesRow(matchingScenario);
    } else {
      const result = await createCustomScenarioData({
        variables: {
          cycleId: selectedCycle.id,
          name: file.name,
          uploader: file.uploader,
          data: file.data,
        },
      });
      if (result.data?.createMmmCustomScenarioData) {
        setSelectedAddToMixesRow(result.data?.createMmmCustomScenarioData);
      }
    }
  };

  const { data: spendData } = useNorthbeamQuery<
    SpendDataColumns,
    SpendDataColumnsVariables
  >(FETCH_SPEND_DATA_COLUMNS, {
    // TODO FIXME!!!!
    // 1. This gets called repeatedly for some reason
    // 2. We should never be using the modelPath for queries. Use the
    //    model ID and get the model path on the backend.
    variables: { modelPath: selectedCycle.model.model_path },
  });

  const spendColumns = useMemo(() => {
    const columns = spendData?.me.mmm.spendDataColumns.spendDataColumns ?? [];
    const idToName: Record<string, string> = {};
    const nameToId: Record<string, string> = {};

    columns.forEach((col) => {
      idToName[col.id] = col.prettyName;
      nameToId[col.prettyName] = col.id;
    });

    return { columns, idToName, nameToId };
  }, [spendData]);

  const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setSelectedFile(null);
      setSelectedAddToMixesRow(null);
      setPrediction(null);
      setBudgetMixName("");
      setIsViewForecastStep(false);
      if (selectedAddToMixesRow) {
        deleteCustomScenarioData({
          variables: { id: selectedAddToMixesRow.id },
        });
      }
    }
  }, [isOpen, selectedAddToMixesRow, deleteCustomScenarioData]);

  const template = useMemo(() => {
    const data: Record<string, string>[] = [];
    const periods = getPeriods(dateRange);

    orderBy(spendColumns.columns, ["prettyName", "asc"]).forEach((column) => {
      const row: Record<string, string> = { Channel: column.prettyName };

      periods.forEach((period) => {
        row[period] = "";
      });

      data.push(row);
    });

    return data;
  }, [dateRange, spendColumns]);

  const selectedKpi = useMemo<Kpi>(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    () => kpis.find((e) => e.kpi_name === selectedKpiName)!,
    [kpis, selectedKpiName],
  );

  const [createCustomBudgetMix] = useNorthbeamMutation<
    CreateCustomBudgetMix,
    CreateCustomBudgetMixVariables
  >(CREATE_CUSTOM_BUDGET_MIX, {
    refetchQueries: [
      {
        query: BUDGET_MIXES_FOR_CYCLE_QUERY,
        variables: {
          id: selectedCycle.id,
          blessed: !isAdminMode,
        },
      },
    ],
  });

  const createBudgetMix = async () => {
    if (!selectedAddToMixesRow) {
      return;
    }

    trackMMMEvent({
      component: CreateCustomBudgetTrackedComponent.ADD_TO_EXPLORE_MIXES_MODAL,
      action: "onSubmit",
    });

    // if there is no set of matching predictions, you can't add to mix
    if (prediction?.file !== selectedAddToMixesRow.name) {
      addToast(
        "We couldn't find a prediction for this upload. Please try again",
        { appearance: "error" },
      );
      return;
    }

    const predictionsByDate = keyBy(prediction.data, "date");

    const { data: budgetMixPayload, error } =
      formatScenarioModellingDataAsBudgetMix(
        selectedAddToMixesRow,
        predictionsByDate,
        kpisByName,
        kpisByName[selectedKpiName],
        selectedCycle,
      );

    if (error || !budgetMixPayload) {
      addToast(error || "Unknown error, please try again", {
        appearance: "error",
      });
      return;
    }

    await Promise.all([
      createCustomBudgetMix({
        variables: {
          cycleId: selectedCycle.id,
          data: {
            ...budgetMixPayload,
            create_budget_recommendations_metadata:
              prediction.create_budget_recommendations_metadata,
          },
          name: budgetMixName,
        },
      }),
      updateCustomScenarioData({
        variables: {
          id: selectedAddToMixesRow.id,
          isAddedToMix: true,
          name: budgetMixName,
        },
      }),
    ]);

    await loadBudgetMixData();

    // HACK to finish fetching the new budget mix before the modal closes
    setTimeout(() => {
      addToast("Scenario added to ‘Explore Budgets’", {
        appearance: "success",
      });

      onClose();
    }, 3_000);
  };

  return (
    <Modal
      size="xl"
      isOpen={isOpen}
      onClose={onClose}
      titleContent={
        <div>
          <div className="font-bold text-xl mb-2">
            Create your own budget mix
          </div>
          <p className="text-xs mb-0">
            Specifications determine the mix we recommended for your review.
          </p>
        </div>
      }
    >
      {isViewForecastStep ? (
        <AnimatePresence>
          <ForecastChart
            selectedKpi={selectedKpi}
            prediction={prediction}
            setPrediction={setPrediction}
            selectedAddToMixesRow={selectedAddToMixesRow}
          />
        </AnimatePresence>
      ) : (
        <div className="space-y-3">
          <div>
            <h4 className="text-lg font-bold mb-4">1. Name your budget</h4>
            <TextInput
              width="300px"
              label="BUDGET NAME*"
              isRequired
              value={budgetMixName}
              onChange={(e) => {
                trackMMMEvent({
                  component:
                    CreateCustomBudgetTrackedComponent.ADD_TO_EXPLORE_MIXES_MODAL,
                  action: "onChange",
                  actionInfo: "Change budget name",
                });

                setBudgetMixName(e.target.value);
              }}
            />
          </div>
          <div className="border-b border-v2-secondary pt-4"></div>
          <div className="flex flex-col">
            <h4 className="text-lg font-bold my-0">2. Target KPI</h4>
            <p className="text-sm mb-4">
              The KPI your budget will be tracked against
            </p>
            <div className="nb-input-container has-label">
              <label className="label block">KPI</label>
              <KpiSelect
                value={selectedKpiName}
                options={kpis.map((e) => ({
                  label: e.friendly_name,
                  value: e.kpi_name,
                }))}
                onChange={(value) => {
                  trackMMMEvent({
                    component:
                      CreateCustomBudgetTrackedComponent.ADD_TO_EXPLORE_MIXES_MODAL,
                    action: "onChange",
                    actionInfo: "Change selected KPI",
                  });
                  setSelectedKpiName(value);
                }}
              />
            </div>
          </div>
          <div className="border-b border-v2-secondary pt-4"></div>
          <div>
            <h4 className="text-lg font-bold my-0">
              3. Download the weekly spend template
            </h4>
            <p className="text-sm mb-6">
              You’ll need to fill out spend data for each week in the upcoming
              period
            </p>
            <CSVDownloader
              data={() => {
                trackMMMEvent({
                  component:
                    CreateCustomBudgetTrackedComponent.UPLOAD_DATA_MODAL,
                  action: "onClick",
                  actionInfo: "Download template CSV",
                });
                return template;
              }}
              type="button"
              filename={`Weekly_Spend_Template_Forecast_${moment().format(
                "YYYY_MM_DD_HH_mm_ss",
              )}`}
              bom={true}
              className="button sm primary-button font-bold flex items-center gap-2"
            >
              <i className="fa-regular fa-sharp fa-download" />
              DOWNLOAD TEMPLATE CSV
            </CSVDownloader>
          </div>
          <div className="border-b border-v2-secondary pt-4"></div>
          <div className="relative pb-4">
            <h4 className="text-lg font-bold my-0">
              4. Upload your completed template
            </h4>
            <p className="text-sm mb-6">Blank cells are treated as $0 spend</p>
            {selectedFile ? (
              <div className="flex flex-col h-32 justify-center p-5 text-sm items-center border border-dashed rounded-lg">
                <div className="bg-v2-primary rounded-3xl flex items-center justify-center flex-col border border-cosmic h-9 w-fit px-2.5 relative">
                  <div
                    className="h-6 w-6 absolute-vertical-center text-white bg-cosmic rounded-full right-1 top-1 cursor-pointer"
                    onClick={() => setSelectedFile(null)}
                  >
                    <i className="fa-regular fa-xmark absolute-center" />
                  </div>
                  <span className="rounded text-2xs px-1.5 py-0.5 mr-4 font-bold">
                    {selectedFile.name}
                  </span>
                </div>
              </div>
            ) : (
              <CSVReader
                style={{
                  dropArea: {
                    borderColor: "#C1C5CD",
                    borderRadius: 8,
                    borderWidth: 1,
                    fontSize: "14px",
                    height: 128,
                  },
                  dropAreaActive: {
                    borderColor: "#7081FF",
                    backgroundColor: "#F3F4FF",
                  },
                  dropFile: {
                    width: "fit-content",
                    height: 36,
                    background: "#F9FAFF",
                    borderColor: "#7081FF",
                    borderWidth: 1,
                    borderRadius: 25,
                    position: "relative",
                  },
                  fileSizeInfo: {
                    display: "none",
                  },
                  fileNameInfo: {
                    color: "#021330",
                    fontSize: 10,
                    fontWeight: "bold",
                  },
                  progressBar: {
                    height: 5,
                    top: 5,
                    position: "absolute",
                    backgroundColor: "#7081FF",
                  },
                }}
                onDrop={(data, file) => {
                  setError(null);

                  if (data.length === 0) {
                    setError("Uploaded file contains no data!");
                    return;
                  }

                  const headers = data[0].data;
                  const periods = headers.slice(1);
                  const channels = data.map((e) => e.data[0]).filter((e) => e);

                  const allowedPeriods = getPeriods(dateRange);
                  const allowedChannels = [
                    "Channel",
                    ...Object.values(spendColumns.idToName),
                  ];

                  const wrongValue = channels.find(
                    (e) => !allowedChannels.includes(e),
                  );

                  if (wrongValue !== undefined) {
                    setError(`"${wrongValue}" is not a valid channel!`);
                    return;
                  }

                  const missingChannel = allowedChannels.find(
                    (e) => !channels.includes(e),
                  );

                  if (missingChannel !== undefined) {
                    setError(`"${missingChannel}" channel is missing!`);
                    return;
                  }

                  if (channels.length !== allowedChannels.length) {
                    setError(
                      "Uploaded file contains wrong number of channels!",
                    );
                    return;
                  }

                  if (periods.length !== allowedPeriods.length) {
                    setError("Uploaded file contains wrong number of weeks!");
                    return;
                  }

                  const invalidDate = periods
                    .map((e) => ({
                      period: e,
                      date: moment(e, TEMPLATE_DATE_FORMAT),
                    }))
                    .find((e) => !e.date.isValid());

                  if (invalidDate) {
                    setError(`"${invalidDate.period}" is not a valid period!`);
                    return;
                  }

                  const invalidPeriod = periods.find(
                    (period) => !allowedPeriods.includes(period),
                  );

                  if (invalidPeriod) {
                    setError(`"${invalidPeriod}" is not a valid period!`);
                    return;
                  }

                  const parseError = data.find((e) => e.errors.length > 0);

                  if (parseError) {
                    setError(`Uploaded file is incorrect: ${parseError}!`);
                    return;
                  }

                  const intermediateResult: Record<
                    string,
                    { channel: string; value: string }[]
                  > = {};

                  for (let i = 1; i < headers.length; i++) {
                    intermediateResult[headers[i]] = [];
                  }

                  for (let i = 1; i < data.length; i++) {
                    const row = data[i].data;
                    const channel = row[0];

                    if (row.length === 1 && row[0] === "") {
                      continue;
                    }

                    for (let j = 1; j < row.length; j++) {
                      intermediateResult[headers[j]].push({
                        channel,
                        value: row[j],
                      });
                    }
                  }

                  const result: UploadedDataRecord[][] = [];
                  let dataError: string | null = null;

                  Object.entries(intermediateResult).forEach(
                    ([period, channels]) => {
                      const week = period.split(" - ");
                      const date = moment(week[0], TEMPLATE_DATE_FORMAT);

                      const resultRow: UploadedDataRecord[] = [
                        {
                          id: "date",
                          label: "date",
                          value: date.format("YYYY-MM-DD"),
                        },
                      ];

                      channels.forEach((channel) => {
                        const spendValue = channel.value.replaceAll(
                          /[^0-9\.\-]/g,
                          "",
                        );

                        if (isNaN(Number(spendValue))) {
                          dataError = `"${channel.value}" is not a number!`;
                          return;
                        }

                        if (Number(spendValue) < 0) {
                          dataError = `"${channel.value}" is a negative number!`;
                          return;
                        }

                        const label = channel.channel;
                        const id = spendColumns.nameToId[label] || label;
                        const value = Number(spendValue).toString();

                        resultRow.push({ id, label, value });
                      });

                      if (dataError) {
                        return;
                      }

                      result.push(resultRow);
                    },
                  );

                  if (dataError) {
                    setError(dataError);
                  } else {
                    setSelectedFile({
                      name: file.name,
                      uploader:
                        user.authedUser.firstName && user.authedUser.lastName
                          ? `${user.authedUser.firstName} ${user.authedUser.lastName}`
                          : user.authedUser.email,
                      data: result,
                    });
                  }
                }}
                onError={(err) => setError(String(err))}
                addRemoveButton
                removeButtonColor="#7081FF"
                onRemoveFile={() => {
                  setSelectedFile(null);
                  setError(null);
                }}
              >
                <i className="fa-solid fa-copy text-2xl my-3" />
                <p className="text-center leading-6">
                  Drag and drop file here or
                  <br />
                  <button className="text-cosmic">Browse computer</button>
                </p>
              </CSVReader>
            )}
            {error && (
              <p className="text-xs text-mars mt-1 mb-0 absolute -bottom-0">
                *{error}
              </p>
            )}
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mt-4">
        <SecondaryButton
          className={clsx({ invisible: !isViewForecastStep })}
          disabled={
            isCreateCustomScenarioDataLoading ||
            isUpdateCustomScenarioLoading ||
            isBudgetMixDataLoading
          }
          onClick={() => setIsViewForecastStep(false)}
        >
          BACK
        </SecondaryButton>
        <div className="flex items-center gap-2">
          <TertiaryButton
            disabled={
              isCreateCustomScenarioDataLoading ||
              isUpdateCustomScenarioLoading ||
              isBudgetMixDataLoading
            }
            loading={
              isUpdateCustomScenarioLoading ||
              isCreateCustomScenarioDataLoading ||
              isBudgetMixDataLoading
            }
            onClick={onClose}
          >
            CANCEL
          </TertiaryButton>
          {isViewForecastStep ? (
            <PrimaryButton
              disabled={
                selectedFile === null ||
                isUpdateCustomScenarioLoading ||
                isCreateCustomScenarioDataLoading ||
                isBudgetMixDataLoading
              }
              loading={
                isUpdateCustomScenarioLoading ||
                isCreateCustomScenarioDataLoading ||
                isBudgetMixDataLoading
              }
              onClick={createBudgetMix}
            >
              CREATE BUDGET MIX
            </PrimaryButton>
          ) : (
            <PrimaryButton
              className="font-bold flex items-center gap-2"
              disabled={
                selectedFile === null ||
                isCreateCustomScenarioDataLoading ||
                !budgetMixName ||
                isBudgetMixDataLoading
              }
              loading={isCreateCustomScenarioDataLoading}
              onClick={() => {
                if (selectedFile) {
                  onAddUpload(selectedFile);
                  setIsViewForecastStep(true);
                }
              }}
            >
              UPLOAD & VIEW FORECAST
            </PrimaryButton>
          )}
        </div>
      </div>
    </Modal>
  );
};
